import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { en } from '../i18n/en';
import { es } from '../i18n/es';

const LanguageContext = createContext();

const translations = { en, es };

export function LanguageProvider({ children }) {
  // Initialize language from the ?lang= URL parameter (used by sitemap hreflang
  // alternates), then localStorage, defaulting to 'en'
  const [language, setLanguage] = useState(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang === 'en' || urlLang === 'es') return urlLang;
    const stored = localStorage.getItem('omg-lang');
    return stored && (stored === 'en' || stored === 'es') ? stored : 'en';
  });

  useEffect(() => {
    // Store language preference and set HTML lang attribute
    localStorage.setItem('omg-lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let result = translations[language];
    for (const k of keys) {
      if (result && typeof result === 'object') {
        result = result[k];
      } else {
        return key;
      }
    }
    // Fall back to the key only when the translation is missing entirely;
    // deliberately empty strings ('') are valid and must not leak raw keys
    return typeof result === 'string' ? result : key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
